import React from 'react';
import { Link } from 'react-router-dom';

export function Footer({ settings }) {
  const phone = settings?.phone || "82 82 10 10";
  const email = settings?.email || "kontakt@fodtid.dk";
  const hours = settings?.hours || "Mandag - Lørdag: 9 - 18";
  const coverage = settings?.coverage_area || "Hele Sjælland – minus øerne";
  const cvr = settings?.cvr || "44137992";
  const address = settings?.address || "Perikumhaven 63A, 2730 Herlev";

  return (
    <footer className="bg-[#1C1917] text-stone-300 pt-16 pb-12 border-t border-stone-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        
        {/* Top grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 pb-12 border-b border-stone-800">
          
          {/* Col 1: Brand & Bio */}
          <div className="lg:col-span-2 space-y-4">
            <Link to="/" className="inline-flex flex-col items-start group focus:outline-none" aria-label="Fodtid Forside">
              <img 
                src="https://assets.auxodev.dk/assets/d4428667-cde9-4c31-9224-88278506a01a/fodtid-logo_94fa7593.png" 
                alt="Fodtid Logo" 
                className="h-14 sm:h-16 md:h-18 w-auto object-contain brightness-0 invert opacity-95 transition-transform group-hover:scale-102"
              />
            </Link>
            
            <p className="text-xs sm:text-sm text-stone-400 leading-relaxed max-w-sm">
              Vi bringer skånsom og professionel fodpleje direkte hjem til din stue. Vores udkørende servicebiler medbringer alt udstyr på hele Sjælland (minus øerne) – trygt, personligt og uden besvær.
            </p>

            <div className="pt-2 flex flex-wrap gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 bg-stone-900 text-[#F7EEDB] px-3 py-1 rounded-full border border-stone-800">
                <i className="fa-solid fa-van-shuttle text-[#A67C3D] text-xs" aria-hidden="true" />
                Klinisk fodbehandling hjemme hos dig
              </span>
              <span className="inline-flex items-center gap-1.5 bg-stone-900 text-[#F7EEDB] px-3 py-1 rounded-full border border-stone-800">
                <i className="fa-regular fa-clock text-[#A67C3D] text-xs" aria-hidden="true" />
                Mandag - Lørdag 9 - 18
              </span>
            </div>
          </div>

          {/* Col 2: Ydelser */}
          <div>
            <h3 className="text-canvas font-semibold text-xs tracking-wider uppercase mb-4 font-sans">
              Behandlinger
            </h3>
            <ul className="space-y-2.5 text-xs text-canvas/70">
              <li>
                <Link to="/ydelser/komplet-fodbehandling" className="hover:text-brand-accent transition-colors">
                  Komplet Fodbehandling (499 kr.)
                </Link>
              </li>
              <li>
                <Link to="/ydelser/abonnement" className="hover:text-brand-accent transition-colors">
                  Fodpleje Abonnement (119 kr./mdr.)
                </Link>
              </li>
              <li>
                <Link to="/ydelser/laserbehandling" className="hover:text-brand-accent transition-colors">
                  Laserbehandling af fødder
                </Link>
              </li>
              <li>
                <Link to="/ydelser/produkter-og-cremer" className="hover:text-brand-accent transition-colors">
                  Naturlige Urtecremer
                </Link>
              </li>
              <li>
                <Link to="/ydelser" className="text-brand-accent hover:underline inline-block pt-1 font-medium">
                  Se alle ydelser →
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3: Hurtige Links */}
          <div>
            <h3 className="text-canvas font-semibold text-xs tracking-wider uppercase mb-4 font-sans">
              Genveje
            </h3>
            <ul className="space-y-2.5 text-xs text-canvas/70">
              <li>
                <Link to="/priser" className="hover:text-brand-accent transition-colors">
                  Priser & Betingelser
                </Link>
              </li>
              <li>
                <Link to="/produkter" className="hover:text-brand-accent transition-colors">
                  Bestil cremer
                </Link>
              </li>
              <li>
                <Link to="/blog" className="hover:text-brand-accent transition-colors">
                  Blog & Videnscenter
                </Link>
              </li>
              <li>
                <Link to="/galleri" className="hover:text-brand-accent transition-colors">
                  Billedgalleri
                </Link>
              </li>
              <li>
                <Link to="/om-os" className="hover:text-brand-accent transition-colors">
                  Om Klaus Just & Fodtid
                </Link>
              </li>
              <li>
                <Link to="/salgs-og-leveringsbetingelser" className="hover:text-brand-accent transition-colors">
                  Salgs- og leveringsbetingelser
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4: Kontakt & Område */}
          <div>
            <h3 className="text-canvas font-semibold text-xs tracking-wider uppercase mb-4 font-sans">
              Kontakt & Område
            </h3>
            <div className="space-y-3 text-xs text-canvas/70">
              <div>
                <span className="text-canvas/50 block text-[10px] uppercase font-bold tracking-wider">Telefon</span>
                <a 
                  href={`tel:${phone.replace(/\s+/g, '')}`} 
                  className="text-canvas hover:text-brand-accent font-semibold text-sm whitespace-nowrap inline-flex items-center gap-1.5 mt-0.5"
                >
                  <i className="fa-solid fa-phone text-brand-accent text-xs" aria-hidden="true" />
                  <span>{phone}</span>
                </a>
              </div>

              <div>
                <span className="text-canvas/50 block text-[10px] uppercase font-bold tracking-wider">Email</span>
                <a 
                  href={`mailto:${email}`} 
                  className="text-canvas hover:text-brand-accent font-medium whitespace-nowrap inline-flex items-center gap-1.5 mt-0.5"
                >
                  <i className="fa-solid fa-envelope text-brand-accent text-xs" aria-hidden="true" />
                  <span>{email}</span>
                </a>
              </div>

              <div>
                <span className="text-canvas/50 block text-[10px] uppercase font-bold tracking-wider">Åbningstider</span>
                <span className="text-canvas/90 font-medium">{hours}</span>
              </div>

              <div>
                <span className="text-canvas/50 block text-[10px] uppercase font-bold tracking-wider">Dækningsområde</span>
                <span className="text-canvas/90 font-medium">{coverage}</span>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom copyright & legal */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-canvas/50">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <span>© {new Date().getFullYear()} Fodtid ApS. Alle rettigheder forbeholdes.</span>
            {cvr && cvr !== "0000000" && (
              <span>CVR: {cvr}</span>
            )}
            {address && (
              <span className="hidden sm:inline">{address}</span>
            )}
          </div>

          <div className="flex items-center gap-4 text-canvas/70">
            <Link to="/salgs-og-leveringsbetingelser" className="hover:text-brand-accent transition-colors">
              Salgs- og leveringsbetingelser
            </Link>
            <span>•</span>
            <Link to="/kontakt" className="hover:text-brand-accent transition-colors">
              Bestil tid
            </Link>
          </div>
        </div>

      </div>
    </footer>
  );
}
